/**
 * Single OAuth connection card with avatar, scope warnings, and disconnect controls.
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';
import type { OAuthConnection, Provider, PurposeType } from './types';
import { connectionNeedsAppInstallation, connectionNeedsRepoScope, getInitials } from './utils';

interface ConnectionCardProps {
  connection: OAuthConnection;
  provider: Provider;
  purposeLabel: string;
  failedAvatars: Set<string>;
  onAvatarError: (avatarUrl: string) => void;
  onConnect: (providerName: string, purpose: PurposeType) => void;
  onDisconnect: (
    connectionId: string,
    providerName: string,
    username: string,
    purpose: string,
  ) => void;
  onInstallGitHubApp: (installationUrl: string) => void;
}

export const ConnectionCard: React.FC<ConnectionCardProps> = ({
  connection,
  provider,
  purposeLabel,
  failedAvatars,
  onAvatarError,
  onConnect,
  onDisconnect,
  onInstallGitHubApp,
}) => {
  const shouldShowAvatar = connection.avatarUrl && !failedAvatars.has(connection.avatarUrl);
  const needsRepoScope = connectionNeedsRepoScope(connection);
  const needsAppInstallation = connectionNeedsAppInstallation(connection);

  return (
    <div className="flex flex-col p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {shouldShowAvatar ? (
            <img
              src={connection.avatarUrl}
              alt={connection.username}
              className="w-10 h-10 rounded-full"
              onError={() => onAvatarError(connection.avatarUrl!)}
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-linear-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xs">
              {getInitials(connection.username || connection.email)}
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {connection.username}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{connection.email}</p>
            <p className="text-xs text-teal-600 dark:text-teal-400 mt-0.5">{purposeLabel}</p>
            {connection.lastSyncedAt && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                Last synced: {new Date(connection.lastSyncedAt).toLocaleDateString()}
              </p>
            )}
            {needsRepoScope && (
              <p className="text-xs font-semibold text-amber-600 dark:text-amber-300 mt-1">
                GitHub repo scope is missing. Reauthorize to view private repositories.
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {needsRepoScope && (
            <button
              onClick={() => onConnect(provider.name, 'both')}
              className="px-3 py-1.5 text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300 rounded-md hover:bg-orange-200 dark:hover:bg-orange-900/30 transition-colors"
            >
              Request repo access
            </button>
          )}
          <button
            onClick={() =>
              onDisconnect(
                connection.id,
                provider.displayName,
                connection.username,
                connection.purpose,
              )
            }
            className="px-3 py-1.5 text-xs bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
          >
            Disconnect
          </button>
        </div>
      </div>

      {/* GitHub App Installation Banner */}
      {needsAppInstallation && connection.appInstallation && (
        <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                GitHub App Not Installed
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                Install the Autar GitHub App to enable webhooks and access organization
                repositories. Without the app installed, you may not see all your private
                repositories.
              </p>
              <button
                onClick={() => {
                  const installUrl = connection.appInstallation?.installationUrl;
                  if (installUrl) {
                    onInstallGitHubApp(installUrl);
                  }
                }}
                disabled={!connection.appInstallation?.installationUrl}
                className="mt-2 px-3 py-1.5 text-xs bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Install GitHub App
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
